import { useFolders } from '@/hooks/useFolders';
import { useCategories } from '@/hooks/useCategories';
import { IBookmark } from '@/common/types/IBookmark';
import {
  TIME_RANGE_LABELS,
  TIME_RANGE_PRESET_ORDER,
  TIME_RANGE_SHORT_LABELS,
  type TimeRangePreset,
} from '@/utilities/bookmark/timeRangeFilter';
import { useSflColors, sflRadius } from '@/styling/sfl';

type Props = {
  bookmarks: IBookmark[];
  selectedFolderId: string | null;
  onFolderChange: (folderId: string | null) => void;
  selectedCategoryId: string | null;
  onCategoryChange: (categoryId: string | null) => void;
  timeRange: TimeRangePreset;
  onTimeRangeChange: (preset: TimeRangePreset) => void;
  variant?: 'default' | 'sidebar';
};

type FieldLabelProps = { children: string; hint?: string; colors: ReturnType<typeof useSflColors> };

function FieldLabel({ children, hint, colors }: FieldLabelProps) {
  return (
    <div className="mb-1.5">
      <label className="block text-xs font-semibold tracking-wide" style={{ color: colors.text }}>
        {children}
      </label>
      {hint ? (
        <p className="text-[11px] leading-snug mt-0.5" style={{ color: colors.text3 }}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export default function BookmarkAdvancedFilters({
  bookmarks,
  selectedFolderId,
  onFolderChange,
  selectedCategoryId,
  onCategoryChange,
  timeRange,
  onTimeRangeChange,
  variant = 'default',
}: Props) {
  const colors = useSflColors();
  const { folders, loading: foldersLoading } = useFolders();
  const { categories } = useCategories(bookmarks);

  const hasActiveFilters =
    selectedFolderId !== null ||
    selectedCategoryId !== null ||
    timeRange !== 'all';

  const isSidebar = variant === 'sidebar';

  const panel = {
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border2}`,
    borderRadius: sflRadius.lg,
    maxWidth: '100%' as const,
  };

  const selectClass =
    'w-full min-h-[40px] px-3 py-2 rounded-[12px] border text-sm font-sans focus:outline-none focus:ring-2 focus:ring-[var(--sfl-accent)] focus:ring-offset-0 focus:border-transparent transition-shadow';

  const selectStyle = {
    backgroundColor: colors.surface2,
    borderColor: colors.border2,
    color: colors.text,
  } as const;

  const clearAll = () => {
    onFolderChange(null);
    onCategoryChange(null);
    onTimeRangeChange('all');
  };

  const timeChips = (
    <div
      className={`grid gap-1.5 ${isSidebar ? 'grid-cols-2' : 'grid-cols-3 sm:grid-cols-6 max-w-3xl'}`}
      role="group"
      aria-label="When saved"
    >
      {TIME_RANGE_PRESET_ORDER.map((preset) => {
        const selected = timeRange === preset;
        return (
          <button
            key={preset}
            type="button"
            onClick={() => onTimeRangeChange(preset)}
            className="min-h-[38px] px-2 text-xs font-medium rounded-[10px] border transition-colors font-sans"
            style={{
              backgroundColor: selected ? colors.accentLight : 'transparent',
              borderColor: selected ? colors.accent : colors.border2,
              color: selected ? colors.accent : colors.text2,
            }}
          >
            {isSidebar ? TIME_RANGE_SHORT_LABELS[preset] : TIME_RANGE_LABELS[preset]}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className={`w-full min-w-0 ${isSidebar ? 'mb-0' : 'mb-6'}`} style={panel}>
      {(
        <div className={isSidebar ? 'p-3 space-y-4' : 'p-4 space-y-5'}>
          <div className={`grid gap-4 ${isSidebar ? '' : 'sm:grid-cols-2'}`}>
          {/* Folder */}
          <div className="min-w-0">
            <FieldLabel colors={colors}>
              Folder
            </FieldLabel>
            <select
              id={isSidebar ? 'adv-folder-sb' : 'adv-folder'}
              disabled={foldersLoading}
              aria-label="Folder"
              className={selectClass}
              style={selectStyle}
              value={selectedFolderId ?? ''}
              onChange={(e) => onFolderChange(e.target.value === '' ? null : e.target.value)}
            >
              <option value="">All folders</option>
              {folders.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                  {f.bookmarkCount != null ? ` (${f.bookmarkCount})` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Topic */}
          <div className="min-w-0">
            <FieldLabel colors={colors}>
              Topic
            </FieldLabel>
            <select
              id={isSidebar ? 'adv-topic-sb' : 'adv-topic'}
              aria-label="Topic"
              className={selectClass}
              style={selectStyle}
              value={selectedCategoryId ?? ''}
              onChange={(e) => onCategoryChange(e.target.value === '' ? null : e.target.value)}
            >
              <option value="">All topics</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.count > 0 ? ` (${c.count})` : ''}
                </option>
              ))}
            </select>
          </div>

          </div>

          {/* Date — full width below folder/topic on wide layout */}
          <div className="min-w-0">
            <FieldLabel colors={colors} hint={isSidebar ? undefined : 'By when the bookmark was added.'}>
              Date saved
            </FieldLabel>
            {timeChips}
          </div>

          {hasActiveFilters && (
            <div
              className={`flex border-t pt-3 ${isSidebar ? 'justify-center' : 'justify-end -mx-4 px-4'}`}
              style={{ borderColor: colors.border2 }}
            >
              <button
                type="button"
                onClick={clearAll}
                className={`text-sm font-medium font-sans py-2.5 px-4 rounded-[12px] transition-opacity hover:opacity-80 min-h-[40px] ${isSidebar ? 'w-full' : ''}`}
                style={{ color: colors.accent, backgroundColor: colors.accentLight }}
              >
                Reset filters
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
